import { useRef, useState } from "react";
import { useIntl } from "react-intl";

import Button from "../ui/button";
import { EditSite } from "../../hooks/useSites";
import { SiteSummary } from "../../types/user";
import { DateDataBaseType } from "../../types/database";

type ManageSiteProps = {
  site: (SiteSummary & DateDataBaseType) | null;
  error?: Error | null;
  success?: { message: string } | null;
  onSave: (action: "ADD" | "UPDATE", site: EditSite) => void;
  onDelete: () => void;
};

export default function ManageSite({ site, error, success, onSave, onDelete }: ManageSiteProps) {
  const intl = useIntl();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  function saveSite() {
    setIsSubmitted(true);
    if (formRef.current?.checkValidity()) {
      onSave(site ? "UPDATE" : "ADD", {
        id: site?.id,
        name: nameRef.current!.value.trim(),
      });
    }
  }

  return (
    <>
      <h3 className="mb-5 text-lg font-normal text-shadow border-b w-fit border-slate-300">
        {site
          ? intl.formatMessage({ id: "sites.manage.editHeading" })
          : intl.formatMessage({ id: "sites.manage.addHeading" })}
      </h3>

      {error && (
        <p className="mb-4 text-sm text-red-600">{error.message}</p>
      )}

      {success && (
        <p className="mb-4 text-sm text-green-600">{success.message}</p>
      )}

      <form ref={formRef}>
        <div className="mb-3">
          <label className="subtitle block" htmlFor="siteName">
            {intl.formatMessage({ id: "sites.manage.nameLabel" })}
          </label>
          <input
            ref={nameRef}
            type="text"
            id="siteName"
            required
            className="input mb-0 peer"
            autoComplete="off"
            placeholder={intl.formatMessage({ id: "sites.manage.namePlaceholder" })}
            defaultValue={site?.name}
          />
          {isSubmitted && (
            <span className="mt-2 hidden peer-invalid:block text-xs text-red-500">
              {intl.formatMessage({ id: "sites.manage.nameRequired" })}
            </span>
          )}
        </div>

        <div className="mt-6 flex gap-3">
          <Button theme="primary" size="small" onClick={saveSite}>
            {site
              ? intl.formatMessage({ id: "sites.manage.save" })
              : intl.formatMessage({ id: "sites.manage.create" })}
          </Button>
          {site && (
            <Button theme="danger" size="small" onClick={onDelete}>
              {intl.formatMessage({ id: "sites.manage.delete" })}
            </Button>
          )}
        </div>
      </form>
    </>
  );
}
